/**
 * Esta semana — la primera tarjeta del paso 3, y la única que hace falta leer.
 *
 * Eduardo (2026-09-06): *"cuando veo el panel no sé si está funcionando bien o
 * no"*. Esto contesta eso en tres líneas: qué hizo la última revisión, qué
 * espera de él, y cuándo es la próxima. Todo lo demás del paso 3 es el
 * detalle de estas tres líneas.
 *
 * Lo que hizo sale del resumen GUARDADO de la revisión (services/maintenance/
 * revision.ts), no se recalcula acá: es lo que pasó ese día, no lo que hay
 * ahora. Lo que espera de él sí se lee en vivo (borradores, artículos,
 * reporte), porque cambia con cada clic suyo.
 */
import { useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { SectionHeaderAction } from '../../ui/SectionHeaderAction.js';
import { Pill } from '../../PillGroup.js';
import { useToast } from '../../ui/Toast.js';
import { useHallazgos } from '../../../hooks/useHallazgos.js';
import { useSiteConnection } from '../../../hooks/useSiteConnection.js';
import {
  useMantenimiento, useRevision, useCorrerRevision, useArticulos, usePeriodo, useNicho,
} from '../../../hooks/useMantenimiento.js';
import type { ResumenDeRevision } from '../../../hooks/useMantenimiento.js';

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

const etiqueta = {
  ...menor,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
} as const;

const cuerpo = {
  fontSize: 'var(--evo-fs-label)',
  color: 'var(--evo-fg-2)',
  fontFamily: 'var(--evo-font)',
  lineHeight: 1.5,
} as const;

/** La fecha de una revisión (YYYY-MM-DD, zona del negocio) como día de la semana y fecha. */
export function fechaLarga(f: string, lang: string): string {
  return new Date(`${f}T12:00:00Z`).toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'short' });
}

export function SeccionEstaSemana({ projectId }: { projectId: number }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const { data: estado } = useMantenimiento(projectId);
  const encendido = estado?.enabled === true;
  const { data: rev } = useRevision(projectId, encendido);
  const correr = useCorrerRevision(projectId);
  const { data: hallazgos } = useHallazgos(projectId, false);
  const { data: arts } = useArticulos(projectId, encendido);
  const { data: periodo } = usePeriodo(projectId, encendido);
  const { data: conexion } = useSiteConnection(projectId);
  const { data: nichoData } = useNicho(projectId, encendido);

  // Cuando la revisión termina, todo lo que dejó cambia: hallazgos, artículos,
  // temas, la serie. Se relee en cuanto el estado pasa de corriendo a lista.
  const estadoPrevio = useRef<string | null>(null);
  useEffect(() => {
    const ahora = rev?.ultima?.estado ?? null;
    if (estadoPrevio.current === 'corriendo' && ahora !== 'corriendo') {
      for (const k of ['hallazgos', 'articulos', 'temas', 'mantenimiento', 'periodo']) {
        void qc.invalidateQueries({ queryKey: [k, projectId] });
      }
    }
    estadoPrevio.current = ahora;
  }, [rev?.ultima?.estado, projectId, qc]);

  if (!encendido) return null;

  const ultima = rev?.ultima ?? null;
  const corriendo = ultima?.estado === 'corriendo';
  const r = ultima?.resumen ?? null;
  const fechaCorta = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' });
  const fmtDia = (f: string) => new Date(`${f}T12:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  // ── Lo que hizo, en frases ─────────────────────────────────────────────────
  const hizo: string[] = r ? frasesDeLoQueHizo(r, t, fmtDia) : [];

  // ── Lo tuyo, en vivo ───────────────────────────────────────────────────────
  const borradores = Object.values(hallazgos?.borradores ?? {});
  const vigentes = new Set((hallazgos?.hallazgos ?? []).map(h => h.id));
  const porAprobar = borradores.filter(b => b.estado === 'propuesta' && vigentes.has(b.hallazgoId)).length;
  const porDecidir = borradores.filter(b => b.estado === 'decidir' && vigentes.has(b.hallazgoId)).length;
  // Un arreglo de velocidad marca varias reglas a la vez: se cuenta por tipo
  // de cambio, no por fila, o "una optimización" salía como "8 cambios".
  const aplicados = new Set(borradores.filter(b => b.estado === 'aplicado').map(b => b.tipo)).size;
  const esperando = (arts?.articulos ?? []).filter(a => a.status === 'borrador' || a.status === 'cambios').length;
  const sinFecha = (arts?.articulos ?? []).filter(a => a.status === 'aprobado' && !a.scheduledFor).length;
  const reporte = periodo?.reportePreparado ?? null;
  // Lo que FRENA la revisión va primero: sin sitio, sin nicho, en manual, con
  // cero por semana no hay ciclo, y ninguna otra tarea lo arregla. Era lo único
  // que «Cómo funciona» aportaba; el resto ya lo dice esta tarjeta.
  const tuyo: string[] = [
    conexion && !conexion.connection?.siteUrl ? t('projects.maintenance.week.yours.noSite') : null,
    nichoData && !nichoData.nicho ? t('projects.maintenance.week.yours.noNiche') : null,
    estado?.autonomia === 'manual' ? t('projects.maintenance.week.yours.manual') : null,
    estado && estado.articulosPorSemana === 0 ? t('projects.maintenance.week.yours.noRhythm') : null,
    porAprobar > 0 ? t('projects.maintenance.week.yours.approveTexts', { count: porAprobar }) : null,
    porDecidir > 0 ? t('projects.maintenance.week.yours.decideTexts', { count: porDecidir }) : null,
    esperando > 0 ? t('projects.maintenance.week.yours.reviewArticles', { count: esperando }) : null,
    sinFecha > 0 ? t('projects.maintenance.week.yours.scheduleArticles', { count: sinFecha }) : null,
    reporte ? t('projects.maintenance.week.yours.sendReport') : null,
    aplicados > 0 ? t('projects.maintenance.week.yours.publish', { count: aplicados }) : null,
  ].filter((x): x is string => x !== null);

  const puntajes = r
    ? [
      r.puntajes.accesibilidad != null ? `${t('projects.design.tab.step3.health.band.accessibility')} ${String(r.puntajes.accesibilidad)}` : null,
      r.puntajes.seo != null ? `${t('projects.design.tab.step3.health.band.seo')} ${String(r.puntajes.seo)}` : null,
      r.puntajes.velocidad != null ? `${t('projects.design.tab.step3.health.band.speed')} ${String(r.puntajes.velocidad)}` : null,
    ].filter(Boolean).join(' · ')
    : '';

  function correrAhora(): void {
    correr.mutate(undefined, {
      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
    });
  }

  const proxima = rev?.proxima ?? null;

  return (
    <DetailSection
      title={t('projects.maintenance.week.title')}
      padding="none"
      titleRight={
        <SectionHeaderAction loading={correr.isPending || corriendo} onClick={correrAhora}>
          {corriendo ? t('projects.maintenance.week.running') : t('projects.maintenance.week.runNow')}
        </SectionHeaderAction>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14em, 1fr))' }}>
        {/* 1 · Lo que hizo la última revisión */}
        <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderRight: '1px solid var(--evo-stroke)', minWidth: 0 }}>
          <div style={etiqueta}>
            {ultima
              ? t('projects.maintenance.week.lastReview', { fecha: fechaCorta(ultima.empezoEn) })
              : t('projects.maintenance.week.didTitle')}
          </div>
          {corriendo
            ? <p style={{ ...cuerpo, margin: 'var(--evo-space-2) 0 0', color: 'var(--evo-cyan)' }}>{t('projects.maintenance.week.runningBody')}</p>
            : !ultima
              ? <p style={{ ...cuerpo, margin: 'var(--evo-space-2) 0 0', color: 'var(--evo-fg-3)' }}>{t('projects.maintenance.week.noReview')}</p>
              : (
                <>
                  {puntajes && <div style={{ ...menor, marginTop: 'var(--evo-space-2)', color: 'var(--evo-fg-3)' }}>{puntajes}</div>}
                  {hizo.length === 0
                    ? <p style={{ ...cuerpo, margin: 'var(--evo-space-1) 0 0', color: 'var(--evo-fg-3)' }}>{t('projects.maintenance.week.nothingDone')}</p>
                    : (
                      <ul style={{ ...cuerpo, margin: 'var(--evo-space-1) 0 0', paddingLeft: '1.1em' }}>
                        {hizo.map(f => <li key={f}>{f}</li>)}
                      </ul>
                    )}
                  {r && r.errores.length > 0 && (
                    <div style={{ ...menor, marginTop: 'var(--evo-space-1)', color: 'var(--evo-amber)' }}>
                      {t('projects.maintenance.week.errors', { count: r.errores.length })}
                    </div>
                  )}
                </>
              )}
        </div>

        {/* 2 · Lo tuyo */}
        <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderRight: '1px solid var(--evo-stroke)', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
            <span style={etiqueta}>{t('projects.maintenance.week.yoursTitle')}</span>
            {tuyo.length > 0 && <Pill tone="amber" variant="outline" size="xs">{String(tuyo.length)}</Pill>}
          </div>
          {tuyo.length === 0
            ? <p style={{ ...cuerpo, margin: 'var(--evo-space-2) 0 0', color: 'var(--evo-fg-3)' }}>{t('projects.maintenance.week.nothingYours')}</p>
            : (
              <ul style={{ ...cuerpo, margin: 'var(--evo-space-2) 0 0', paddingLeft: '1.1em', color: 'var(--evo-fg-1)' }}>
                {tuyo.map(f => <li key={f}>{f}</li>)}
              </ul>
            )}
        </div>

        {/* 3 · La próxima */}
        <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', minWidth: 0 }}>
          <div style={etiqueta}>{t('projects.maintenance.week.nextTitle')}</div>
          <p style={{ ...cuerpo, margin: 'var(--evo-space-2) 0 0' }}>
            {proxima
              ? t('projects.maintenance.week.nextReview', { fecha: fechaLarga(proxima.fecha, i18n.language), hora: String(proxima.hora).padStart(2, '0') })
              : t('projects.maintenance.week.noNext')}
          </p>
          <p style={{ ...menor, margin: 'var(--evo-space-1) 0 0', maxWidth: '40ch', lineHeight: 1.5 }}>
            {t('projects.maintenance.week.nextHint', { count: estado?.articulosPorSemana ?? 0 })}
          </p>
        </div>
      </div>
    </DetailSection>
  );
}

/** Las frases del resumen, en el orden en que se leen. Misma lógica que aviso.ts del servidor. */
function frasesDeLoQueHizo(r: ResumenDeRevision, t: TFunction, fmtDia: (f: string) => string): string[] {
  const f: string[] = [];
  const k = 'projects.maintenance.week.did';
  if (r.arreglosAutomaticos.length > 0) f.push(t(`${k}.fixes`, { count: r.arreglosAutomaticos.length }));
  const b = r.borradores;
  if (b.aplicados > 0) f.push(t(`${k}.applied`, { count: b.aplicados }));
  if (b.propuesta > 0) f.push(t(`${k}.proposed`, { count: b.propuesta }));
  if (b.decidir > 0) f.push(t(`${k}.decide`, { count: b.decidir }));
  if (b.bien > 0) f.push(t(`${k}.fine`, { count: b.bien }));
  if (r.temas.total > 0) f.push(t(`${k}.topics`, { total: r.temas.total, nuevos: r.temas.nuevos }));
  else if (r.temas.motivo) f.push(t(`${k}.noTopics`, { porque: t(`projects.maintenance.topics.reason.${r.temas.motivo}`, { defaultValue: r.temas.motivo }) }));
  if (r.articulos.escritos.length > 0) {
    const fechas = r.articulos.fechas;
    f.push(t(`${k}.articles`, { count: r.articulos.escritos.length, desde: fmtDia(fechas[0] ?? ''), hasta: fmtDia(fechas[fechas.length - 1] ?? '') }));
  } else if (r.articulos.motivo === 'esperando-revision') f.push(t(`${k}.articlesWaiting`, { count: r.articulos.esperandoDeAntes }));
  else if (r.articulos.motivo === 'sin-ritmo') f.push(t(`${k}.articlesNoRhythm`));
  else if (r.articulos.motivo === 'sin-temas') f.push(t(`${k}.articlesNoTopics`));
  if (r.reporte.reportId) f.push(t(r.reporte.enviado ? `${k}.reportSent` : `${k}.reportPrepared`));
  return f;
}
